import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import LoginModal from 'src/components/LoginModal';
import useLogin from 'src/hooks/useLogin';
import { GetGoogleUserInfoAPI } from 'src/stores/auth/apis';
import { isLoginLoadingAtom, socialTokenAtom, tryLoginTypeAtom } from 'src/stores/auth/atoms';
import { showModalAtom } from 'src/stores/modal/atoms';
import { showToastAtom } from 'src/stores/toast/atoms';
import { ModalType } from 'src/types/Modal';
import { LoginType } from 'src/types/register';
import { ToastType } from 'src/types/Toast';

/**
 * PWA Google OAuth redirect 콜백 핸들러.
 * redirect 모드에서 Google이 `?code=xxx&scope=xxx`를 URL에 붙여서 돌아오므로,
 * 페이지 로드 시 code 파라미터를 감지하여 로그인 처리한다.
 */
const GoogleOAuthRedirectHandler = () => {
  const router = useRouter();
  const {
    loginOAuth
  } = useLogin();
  const showToast = useSetAtom(showToastAtom);
  const showModal = useSetAtom(showModalAtom);
  const setIsLoginLoading = useSetAtom(isLoginLoadingAtom);
  const setTryLoginType = useSetAtom(tryLoginTypeAtom);
  const setSocialToken = useSetAtom(socialTokenAtom);
  const handledRef = useRef(false);
  useEffect(() => {
    if (handledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const scope = params.get('scope');
    if (!code || !scope?.includes('googleapis.com')) return;
    handledRef.current = true;
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);
    showModal({
      key: ModalType.LOGIN,
      component: LoginModal
    });
    setIsLoginLoading(true);
    (async () => {
      try {
        const [{
          data: {
            accessToken
          }
        }] = await GetGoogleUserInfoAPI(code, location.origin);
        setTryLoginType(LoginType.GOOGLE);
        setSocialToken({
          accessToken
        });
        loginOAuth({
          accessToken
        });
      } catch (error) {
        setIsLoginLoading(false);
        showToast({
          type: ToastType.ERROR,
          message: 'LOGIN__ERROR'
        });
        Sentry.captureException('[Google Login Redirect] - /api/v1/accounts/google/token API Error', {
          extra: {
            error
          }
        });
      }
    })();
  }, [router, loginOAuth, showToast, showModal, setIsLoginLoading, setTryLoginType, setSocialToken]);
  return null;
};
export default GoogleOAuthRedirectHandler;